// @need-translate
import {useRouter} from 'next/router';
import {useCallback, useEffect, useRef, useState} from 'react';
import {OnlyInLoginChecker} from '../../../../../../common/checkers/OnlyInLoginChecker';
import {requestContext} from '../../../../../../common/requestContext';
import {twMerge} from '../../../../../../common/twMerge';
import useBlockContextMenuHook from '../../../../../../common/useBlockContextMenu.hook';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {ArrowDirection} from '../../../../../../design-system/molecules/Tooltip/ArrowDirection.enum';
import {Tooltip} from '../../../../../../design-system/molecules/Tooltip/Tooltip';
import {ApiErrorBoundary} from '../../../../../../errors/ApiErrorBoundary.class';
import {TalkViewerErrorBoundary} from '../../../../../../errors/TalkViewerErrorBoundary.class';
import {SlideType} from '../../../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {currentEventContext} from '../../../../../../logging/variables';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {viewerHistory} from '../constant';
import {
  LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED,
  SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME,
  SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME,
  SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME,
  VIEWER_MENU_FIRST_EXPOSE_TIME,
} from '../constant/viewerConstant';
import {ImageViewerFetcherHeadless} from '../headless/ImageViewerFetcherHeadless';
import {MyRatingFetcherHeadless} from '../headless/MyRatingFetcherHeadless';
import {TalkViewerFetcherHeadless} from '../headless/TalkViewerFetcherHeadless';
import {TextViewerFetcherHeadless} from '../headless/TextViewerFetcherHeadless';
import {VideoViewerFetcherHeadless} from '../headless/VideoViewerFetcherHeadless';
import {ViewerEndFetcherHeadless} from '../headless/ViewerEndFetcherHeadless';
import {useRatingDialog} from '../hook/useRatingDialog';
import {useSeriesLikeHook} from '../hook/useSeriesLike';
import useWatermark from '../hook/useWatermark';
import {
  setCanPullToRefresh,
  setCurrentViewerGuide,
  setHideAgeGradeLabel,
  setIsViewerToolTipExposed,
  setNonstopWatching,
  setViewerContinued,
} from '../state/viewerInfoSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {showViewerGuide} from '../util/showViewerGuide';
import {ImageViewerContainer} from './ImageViewerContainer';
import {MobileViewerNavMenuContainer} from './MobileViewerNavMenuContainer';
import {TalkViewerContainer} from './TalkViewerContainer';
import {TalkViewerEndContainer} from './TalkViewerEndContainer';
import {TextViewerContainer} from './TextViewerContainer';
import {VideoViewerContainer} from './VideoViewerContainer';
import {ViewerEndContainer} from './ViewerEndContainer';
import {ViewerSettingContainer} from './ViewerSettingContainer';

interface ViewerContainerProps {
  seriesId: number;
  productId: number;
}

export function ViewerContainer({seriesId, productId}: ViewerContainerProps) {
  useBlockContextMenuHook();
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = requestContext.requestInfo.display.isMobile;
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const {isViewerMenuExposed, isToolTipExposed, isNonstopWatching} =
    useTypedSelector(state => ({
      isViewerMenuExposed: state.viewerMenu.isExposed,
      isToolTipExposed: state.viewerInfo.isToolTipExposed,
      isNonstopWatching:
        !!state.viewerInfo.nonstopWatchingInSeries[seriesId]?.isNonstopWatching,
    }));

  const {data} = useViewerInfoQuery({
    seriesId,
    productId,
  });
  const viewerData = data;
  const isShortsDrama = viewerData?.item.isShortsDrama;

  const {RatingDialog, onClickStar} = useRatingDialog({
    productId,
    seriesId,
  });

  const {textViewerSettings, imageViewerSettings} = useTypedSelector(state => ({
    textViewerSettings: state.textViewer.settings,
    imageViewerSettings: state.imageViewer.settings,
  }));

  const isTextViewer = viewerData?.item.slideType === SlideType.Epub;

  const {prevViewerGuide, currentViewerGuide, viewerContinued} =
    useTypedSelector(state => ({
      prevViewerGuide: state.viewerInfo.prevViewerGuide,
      currentViewerGuide: state.viewerInfo.currentViewerGuide,
      viewerContinued: state.viewerInfo.viewerContinued,
    }));

  const showViewerGuideWhenDifferent = useCallback(() => {
    if (currentViewerGuide && prevViewerGuide !== currentViewerGuide) {
      showViewerGuide({viewerGuideType: currentViewerGuide});
    }
    dispatch(setCurrentViewerGuide(currentViewerGuide));
  }, [currentViewerGuide, prevViewerGuide, dispatch]);

  const [isPcSettingExposed, setIsPcSettingExposed] = useState<boolean>(false);

  const handleClickPcSettingButton = useCallback(() => {
    setIsPcSettingExposed(true);
    //뷰어 설정이 열리고 툴팁이 열려 있다면 툴팁을 닫습니다
    if (isToolTipExposed) {
      dispatch(setIsViewerToolTipExposed(false));
      window.localStorage.setItem(
        LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED,
        'true'
      );
    }
  }, [dispatch, isToolTipExposed]);
  const handleClickPcSettingClose = useCallback(() => {
    showViewerGuideWhenDifferent();
    setIsPcSettingExposed(false);
  }, [showViewerGuideWhenDifferent]);
  const onClickPcGoToStartButton = useCallback(() => {
    if (isTextViewer) {
      window.dispatchEvent(new Event(SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME));
    } else {
      if (imageViewerSettings.readType === ImageViewerReadType.Scroll) {
        window.dispatchEvent(new Event(SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME));
      } else {
        window.dispatchEvent(new Event(SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME));
      }
    }
  }, [imageViewerSettings.readType, isTextViewer]);

  const sendLogAndResetContext = async () => {
    const viewerConversionInfo = currentEventContext.viewerConversionInfo;
    const helixConversionInfo = currentEventContext.helixConversionInfo;
    // 뷰어 오픈 이벤트 로그를 쏘는 부분. 웹뷰인경우는 앱에서 처리하므로 웹뷰인 경우를 제외하고 보냅니다.
    if (!requestContext.requestInfo.display.isWebView) {
      await aggregateAndSendLog({
        targetElem: viewerRef.current as HTMLElement,
        actionType: 'Event',
        actionName: '뷰어_오픈',
        customProps: {
          user_action: 'read',
          viewer_continued: String(viewerContinued + 1),
          is_shortdrama: isShortsDrama ? 'y' : undefined,
          ...viewerConversionInfo,
          ...helixConversionInfo,
        },
      });
    }
    // 열람전환로그, 헬릭스전환로그는 뷰어 오픈 이벤트 발생시 리셋
    currentEventContext.viewerConversionInfo = undefined;
    currentEventContext.helixConversionInfo = undefined;
  };

  const hideViewerMenu = () => {
    const timer = setTimeout(() => {
      if (
        viewerData?.item.slideType !== SlideType.TalkHumor &&
        viewerData?.item.slideType !== SlideType.TalkNovel
      ) {
        dispatch(setIsViewerMenuExposed({isExposed: false}));
      }
    }, VIEWER_MENU_FIRST_EXPOSE_TIME);

    return timer;
  };

  // 처음에 마운트 되었을때만 실행 componentDidMount
  useEffect(() => {
    sendLogAndResetContext();
    dispatch(setViewerContinued({viewerContinued: viewerContinued + 1}));
    showViewerGuideWhenDifferent();

    // 영상 뷰어, 톡뷰어 제외하고 2초후 뷰어 메뉴 숨김
    const timer = hideViewerMenu();
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setCanPullToRefresh(!isShortsDrama));
  }, [dispatch, isShortsDrama]);

  // 뷰어 이탈 시 정주행/헬릭스 정보 초기화
  const handleRouteChangeStart = useCallback(
    (url: string) => {
      // 시리즈 아이디가 같은 뷰어간의 이동에는 정주행 상태를 변경하지 않으며 연령 레이블을 숨김.
      if (
        !url.includes('content') ||
        !url.includes('viewer') ||
        seriesId.toString() !== url.split('/')[2]
      ) {
        // 뷰어 이탈 시에 정주행 상태 초기화
        dispatch(setNonstopWatching({seriesId, isNonstopWatching: false}));
        dispatch(setViewerContinued({viewerContinued: 0}));
      }
    },
    [dispatch, seriesId]
  );

  useEffect(() => {
    if (viewerHistory.length <= 1) {
      dispatch(setHideAgeGradeLabel(false));
    } else {
      dispatch(setHideAgeGradeLabel(true));
    }
  }, [dispatch]);

  useEffect(() => {
    if (router.isReady) {
      router.events.on('routeChangeStart', handleRouteChangeStart);
    }
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [handleRouteChangeStart, router]);

  const onClickTooltipCancel = useCallback(() => {
    dispatch(setIsViewerToolTipExposed(false));
    window.localStorage.setItem(LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED, 'true');
  }, [dispatch]);

  useSeriesLikeHook({seriesId, productId});
  useWatermark();

  if (!viewerData) {
    return null;
  }

  return (
    <div
      className="relative flex h-full grow flex-col"
      {...putTiaraObjectProps({
        customProps: {
          viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
        },
      })}
      ref={viewerRef}
    >
      {/* 상단 navBar 영역은 LayoutViewerHeader에서 처리합니다. */}
      {(viewerData.item.slideType === SlideType.VideoOnly ||
        viewerData.item.slideType === SlideType.TalkHumor) && (
        <VideoViewerFetcherHeadless seriesId={seriesId} productId={productId}>
          <VideoViewerContainer seriesId={seriesId} productId={productId}>
            <ApiErrorBoundary>
              <OnlyInLoginChecker>
                <MyRatingFetcherHeadless
                  seriesId={seriesId}
                  productId={productId}
                />
              </OnlyInLoginChecker>
              {!isShortsDrama && (
                <ViewerEndContainer
                  seriesId={seriesId}
                  productId={productId}
                  onClickStar={onClickStar}
                />
              )}
            </ApiErrorBoundary>
          </VideoViewerContainer>
        </VideoViewerFetcherHeadless>
      )}
      {viewerData.item.slideType === SlideType.TalkNovel && (
        <ApiErrorBoundary>
          <TalkViewerErrorBoundary>
            <TalkViewerFetcherHeadless
              seriesId={seriesId}
              productId={productId}
            >
              <TalkViewerContainer seriesId={seriesId} productId={productId}>
                <OnlyInLoginChecker>
                  <MyRatingFetcherHeadless
                    seriesId={seriesId}
                    productId={productId}
                  />
                </OnlyInLoginChecker>
                <TalkViewerEndContainer
                  seriesId={seriesId}
                  productId={productId}
                  onClickStar={onClickStar}
                />
              </TalkViewerContainer>
            </TalkViewerFetcherHeadless>
          </TalkViewerErrorBoundary>
        </ApiErrorBoundary>
      )}
      {viewerData.item.slideType === SlideType.Comic && (
        <ApiErrorBoundary>
          <ImageViewerFetcherHeadless seriesId={seriesId} productId={productId}>
            <ImageViewerContainer seriesId={seriesId} productId={productId}>
              <OnlyInLoginChecker>
                <MyRatingFetcherHeadless
                  seriesId={seriesId}
                  productId={productId}
                />
              </OnlyInLoginChecker>
              <ViewerEndContainer
                seriesId={seriesId}
                productId={productId}
                onClickStar={onClickStar}
                className="m-auto w-full"
              />
            </ImageViewerContainer>
          </ImageViewerFetcherHeadless>
        </ApiErrorBoundary>
      )}
      {viewerData.item.slideType === SlideType.Epub && (
        <ApiErrorBoundary>
          <TextViewerFetcherHeadless
            seriesId={seriesId}
            productId={productId}
            viewerInfoData={viewerData}
          >
            <TextViewerContainer
              seriesId={seriesId}
              productId={productId}
              isCmsPreview={false}
              isPreview={false}
            >
              <OnlyInLoginChecker>
                <MyRatingFetcherHeadless
                  seriesId={seriesId}
                  productId={productId}
                />
              </OnlyInLoginChecker>
              <ViewerEndContainer
                seriesId={seriesId}
                productId={productId}
                onClickStar={onClickStar}
                className="m-auto w-full"
              />
            </TextViewerContainer>
          </TextViewerFetcherHeadless>
        </ApiErrorBoundary>
      )}
      {/**
       * 하단 navBar 영역
       * iOS 16버전 스크롤 텍스트 뷰어 하단 메뉴 안나오는 버그(KW-7847)로 인해 텍스트뷰어만 시크바와 같은 타이밍에 뷰어 메뉴를 그립니다.
       */}
      {viewerData?.item.slideType !== SlideType.Epub && !isShortsDrama && (
        <MobileViewerNavMenuContainer
          seriesId={seriesId}
          productId={productId}
        />
      )}
      {/* PC버전 플로팅 버튼 */}
      {!isMobile &&
        viewerData.item.slideType !== SlideType.TalkNovel &&
        viewerData.item.slideType !== SlideType.TalkHumor && (
          <div
            className="fixed bottom-40pxr right-40pxr z-100"
            {...putTiaraObjectProps({
              click: {
                layer1: '뷰어메뉴',
              },
            })}
          >
            {/* TODO : 책갈피 기능이 추가되면 플로팅 버튼이 추가되어야합니다 */}
            <div
              className="mb-10pxr flex h-48pxr w-48pxr cursor-pointer items-center justify-center rounded-full border-1 border-theme-transparent-20 bg-theme-bg-base"
              onClick={e =>
                LOG_CLICK_EVENT('뷰어설정_클릭', e, handleClickPcSettingButton)
              }
            >
              <Icon
                type={
                  viewerData.item.slideType === SlideType.Epub
                    ? IconType.NavigationSettingEpub
                    : IconType.NavigationSetting
                }
                disableActiveEffect
              />
            </div>
            <div
              className="flex h-48pxr w-48pxr cursor-pointer items-center justify-center rounded-full border-1 border-theme-transparent-20 bg-theme-bg-base"
              onClick={onClickPcGoToStartButton}
            >
              <Icon
                type={
                  isTextViewer
                    ? textViewerSettings.readType === TextViewerReadType.Scroll
                      ? IconType.ViewerTop
                      : IconType.ViewerStart
                    : imageViewerSettings.readType ===
                        ImageViewerReadType.Scroll
                      ? IconType.ViewerTop
                      : IconType.ViewerStart
                }
                disableActiveEffect
              />
            </div>
          </div>
        )}
      {/* 뷰어 설정 Dialog(Mobile) or Floating(PC) */}
      {!isMobile && isPcSettingExposed && (
        <>
          {/* 뷰어 설정 배경 */}
          <div
            className="fixed left-0 top-0 z-100 h-full w-full"
            onClick={handleClickPcSettingClose}
          />
          <div
            className="fixed bottom-40pxr right-40pxr z-150 w-360pxr rounded-16pxr bg-theme-bg-surface py-24pxr shadow-dialog"
            {...putTiaraObjectProps({
              click: {
                layer1: '뷰어설정',
              },
            })}
          >
            <div className="mb-10pxr flex h-44pxr items-center justify-between pl-24pxr pr-16pxr">
              <Text className="font-medium2-bold text-theme-solid-100">
                뷰어 설정
              </Text>
              <Icon
                type={IconType.NavigationClose}
                className="cursor-pointer"
                onClick={handleClickPcSettingClose}
              />
            </div>
            <ViewerSettingContainer
              seriesId={seriesId}
              productId={productId}
              slideType={viewerData.item.slideType}
            />
          </div>
        </>
      )}
      {/* 뷰어 툴팁 */}
      {isToolTipExposed && (
        <div
          className={twMerge(
            'fixed z-150',
            isMobile ? 'right-[8px] top-[40px]' : 'bottom-[98px] right-[88px]'
          )}
        >
          <Tooltip
            text={'테마, 글꼴, 글자 크기를\n변경해보세요!'}
            showTooltip={
              isToolTipExposed && (isMobile ? isViewerMenuExposed : true)
            }
            isTailEnd={isMobile}
            arrowDirection={
              isMobile ? ArrowDirection.Top : ArrowDirection.Right
            }
            onClickTooltipCancel={onClickTooltipCancel}
          />
        </div>
      )}
      {/*
        미로그인 시 별점주기 액션을 수행할 때 작품 열람이 없는 경우, 뷰어엔드가 아닌 첫 페이지에서 별점주기 팝업이 떠야 하기 위해 뷰어 바깥에서 별점주기 팝업을 호출합니다.
        ViewerEndFetcher를 공통으로 호출하고 있어, 각 뷰어의 뷰어엔드에서 중복으로 호출하지 않도록 합니다.
       */}
      <ViewerEndFetcherHeadless seriesId={seriesId} productId={productId}>
        <RatingDialog />
      </ViewerEndFetcherHeadless>
    </div>
  );
}
